import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Clock, CheckCircle, AlertTriangle, Ban, Zap, Calendar, Users, Building2, Plus, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: { bg: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  trial_active: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  expiring_soon: { bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  expired: { bg: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  active: { bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  suspended: { bg: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

function KPICard({ icon: Icon, title, count, color, isActive, onClick }) {
  return (
    <button onClick={onClick} className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-200 ${isActive ? 'border-primary shadow-lg scale-[1.02]' : 'border-transparent hover:border-border hover:shadow-md'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${color}`}><Icon className="h-4 w-4 text-white" /></div>
        {isActive && <Badge variant="secondary" className="text-[10px]">Filtered</Badge>}
      </div>
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="text-2xl font-display font-bold tabular-nums">{count}</p>
    </button>
  );
}

export default function AdminTrials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [extendDialog, setExtendDialog] = useState(null);
  const [extendDays, setExtendDays] = useState('7');

  const fetchData = useCallback(async () => {
    try { const res = await api.get('/super-admin/trials'); setData(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExtend = async () => {
    if (!extendDialog) return;
    try {
      const res = await api.post(`/super-admin/trials/${extendDialog.id}/extend`, { days: parseInt(extendDays) || 7 });
      toast.success(res.data.message);
      setExtendDialog(null);
      fetchData();
    } catch (err) { toast.error('Failed to extend trial'); }
  };

  const getFiltered = () => {
    if (!data) return [];
    if (filter === 'all') return [...(data.academies.pending || []), ...(data.academies.trial_active || []), ...(data.academies.expiring_soon || []), ...(data.academies.expired || []), ...(data.academies.active || []), ...(data.academies.suspended || [])];
    return data.academies[filter] || [];
  };

  const filtered = getFiltered();

  if (loading) return (<div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-3 lg:grid-cols-6 gap-3">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>);

  const c = data?.counts || {};

  return (
    <div className="space-y-5" data-testid="admin-trials-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Trial Monitor</h1>
        <p className="text-sm text-muted-foreground">Track academy trials, conversions & suspensions</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard icon={Building2} title="Pending" count={c.pending || 0} color="bg-slate-500" isActive={filter === 'pending'} onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')} />
        <KPICard icon={Clock} title="Trial Active" count={c.trial_active || 0} color="bg-emerald-600" isActive={filter === 'trial_active'} onClick={() => setFilter(filter === 'trial_active' ? 'all' : 'trial_active')} />
        <KPICard icon={AlertTriangle} title="Expiring (3d)" count={c.expiring_soon || 0} color="bg-amber-600" isActive={filter === 'expiring_soon'} onClick={() => setFilter(filter === 'expiring_soon' ? 'all' : 'expiring_soon')} />
        <KPICard icon={Ban} title="Expired" count={c.expired || 0} color="bg-red-600" isActive={filter === 'expired'} onClick={() => setFilter(filter === 'expired' ? 'all' : 'expired')} />
        <KPICard icon={CheckCircle} title="Active (Paid)" count={c.active || 0} color="bg-blue-600" isActive={filter === 'active'} onClick={() => setFilter(filter === 'active' ? 'all' : 'active')} />
        <KPICard icon={Ban} title="Suspended" count={c.suspended || 0} color="bg-red-800" isActive={filter === 'suspended'} onClick={() => setFilter(filter === 'suspended' ? 'all' : 'suspended')} />
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">{filter === 'all' ? 'All Academies' : filter.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} <Badge variant="secondary" className="ml-1">{filtered.length}</Badge></CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">ID</TableHead>
                <TableHead>Academy</TableHead>
                <TableHead className="hidden md:table-cell">City</TableHead>
                <TableHead>Students</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Trial Ends</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No academies in this category</TableCell></TableRow>
              ) : filtered.map(a => {
                const st = a.subscription_status || 'TRIAL';
                const stKey = st.toLowerCase();
                const stCfg = statusColors[stKey] || statusColors.trial_active;
                const trialEnd = a.trial_end_date ? new Date(a.trial_end_date) : null;
                const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : null;

                return (
                  <TableRow key={a.id}>
                    <TableCell><span className="text-xs font-mono text-muted-foreground">{a.custom_id || '-'}</span></TableCell>
                    <TableCell><p className="font-medium text-sm">{a.name}</p></TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{a.city || '-'}</TableCell>
                    <TableCell className="tabular-nums">{a.student_count}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${stCfg.bg}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${stCfg.dot}`} />{st}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm">
                      {trialEnd ? (
                        <div>
                          <span>{trialEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          {daysLeft !== null && daysLeft <= 3 && daysLeft > 0 && <Badge variant="destructive" className="ml-1 text-[10px]">{daysLeft}d</Badge>}
                          {daysLeft === 0 && <Badge variant="destructive" className="ml-1 text-[10px]">Today!</Badge>}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(st === 'TRIAL' || st === 'SUSPENDED' || st === 'PENDING_VERIFICATION') && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setExtendDialog(a); setExtendDays('7'); }}>
                            <Plus className="h-3 w-3 mr-0.5" /> Extend
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Extend Trial Dialog */}
      <Dialog open={!!extendDialog} onOpenChange={() => setExtendDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Extend Trial — {extendDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Current status: <Badge variant="outline" className="ml-1">{extendDialog?.subscription_status}</Badge></p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Extend by (days)</label>
              <Input type="number" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} min="1" max="90" />
            </div>
            <Button className="w-full" onClick={handleExtend}><Clock className="h-4 w-4 mr-2" /> Extend Trial by {extendDays} Days</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
